import { LegacyApi } from "./LegacyApi";
import * as M from "../models";
import { TokenAuthorizationResult } from "../models/auth";
import { RequestInitExtended } from "../models/generic";
import { IResult, Result } from "../models/result";
import Settings from "../settings";
import { AppState } from "../store";
import { Utility } from "../utils";
import { Log } from "../utils/Log";

export type ApiAuthorizeGrantType = "password" | "refresh_token" | "legacy";

export interface ApiAuthorizeOptions {
    clientId: string;
    clientSecret: string;
    username?: string;
    password?: string;
    grantType: ApiAuthorizeGrantType;
    refreshToken?: string;
    legacyMember?: string;
    legacyUser?: string;
}

export interface IApiEndpoint {
    auth: boolean;
    url: string;
}

export class ApiEndpoint implements IApiEndpoint {

    /**
     * Returns a formatted url from given endpoint. If endpoint contains
     * route params, values will be applied.
     * @param ApiEndpoint endpoint
     */
    public static getFormattedUrl = (endpoint: ApiEndpoint) => {
        if (!endpoint.routeParams)
            return endpoint.url;

        // get all route params from url (i.e. /url/{id})
        const regex = /\{.*?\}/g;
        let url = endpoint.url + "";
        let m;

        do {
            m = regex.exec(endpoint.url);
            if (m) {
                const key = m[0].replace(/\{|\}/g, "");
                const val = endpoint.routeParams[key];
                url = url.replace(`{${key}}`, val);
            }
        } while (m);

        return url;
    }

    public auth: boolean;
    public url: string;
    public routeParams?: object;

    public constructor(url: string, auth: boolean = true) {
        this.url = url;
        this.auth = auth;
    }

    public toString = (): string => {
        return this.url;
    }

    public absoluteApiUrl = (): string => {
        return Settings.ApiUrl + ApiEndpoint.getFormattedUrl(this);
    }

    public absoluteServicesUrl = (): string => {
        return Settings.ServicesUrl + ApiEndpoint.getFormattedUrl(this);
    }

    public withUrlParams(params: object) {
        const __this = this;
        const copied = Utility.cloneInstance<ApiEndpoint>(__this);
        copied.routeParams = params;
        return copied;
    }
}

export class TypedApiEndpoint<T> extends ApiEndpoint {
    public withUrlParams(params: object) {
        const __this = this;
        const copied = Utility.cloneInstance<TypedApiEndpoint<T>>(__this);
        copied.routeParams = params;
        return copied;
    }
}

export class Api {

    public static endpoints = {
        // all endpoints that require admin access permissions
        admin: {
            abstract: {
                abstract: new TypedApiEndpoint<M.Abstract.Abstract>("/admin/abstract/{guid}"),
                abstractClassification: new ApiEndpoint("/admin/abstract/abstract-classification"),
                abstractGradingGroup: new ApiEndpoint("/admin/abstract/abstract-grading-group"),
                abstractPlacement: new ApiEndpoint("/admin/abstract/abstract-placement"),
                abstractStatus: new ApiEndpoint("/admin/abstract/abstract-status"),
                batchDelete: new TypedApiEndpoint<number>("/admin/abstract/batch-delete"),
                batchSendFdRequest: new TypedApiEndpoint<M.Abstract.AbstractAuthor[]>("/admin/abstract/batch-send-fd-request"),
                coi: new TypedApiEndpoint<boolean>("/admin/abstract/{guid}/coi"),
                event: new TypedApiEndpoint<M.Abstract.AbstractEvent>("/admin/abstract/event"),
                exportAbstractCME: new TypedApiEndpoint<string>("/admin/abstract/export-abstract-cme/{eventId}"),
                exportAbstracts: new TypedApiEndpoint<string>("/admin/abstract/export-abstracts/{eventId}"),
                exportAbstractGrades: new TypedApiEndpoint<string>("/admin/abstract/export-abstract-grades/{eventId}"),
                exportAbstractReviewers: new TypedApiEndpoint<string>("/admin/abstract/export-abstract-reviewers/{eventId}"),
                exportAbstractsToPDF: new TypedApiEndpoint<string>("/admin/abstract/export-pdf/{eventId}"),
                exportDisclosures: new TypedApiEndpoint<string>("/admin/abstract/export-disclosures/{eventId}"),
                financialDisclosures: new TypedApiEndpoint<M.FinancialDisclosure.FD[]>("/admin/abstract/financial-disclosures"),
                invitationEmailPreview: new TypedApiEndpoint<string>("/admin/abstract/{eventId}/invitation-email-preview/{typeId}"),
                invitationEmailStatus: new TypedApiEndpoint<M.Abstract.InvitationEmailStatus[]>("/admin/abstract/{eventId}/invitation-email-status"),
                invitationEmailTest: new TypedApiEndpoint<boolean>("/admin/abstract/{eventId}/invitation-email-test"),
                list: new TypedApiEndpoint<M.Abstract.AbstractListItem[]>("/admin/abstract"),
                saveAuthorship: new TypedApiEndpoint<M.Abstract.Abstract>("/admin/abstract/authorship"),
                saveDetails: new TypedApiEndpoint<M.Abstract.Abstract>("/admin/abstract/{guid}/details"),
                savePermissions: new TypedApiEndpoint<M.Abstract.Abstract>("/admin/abstract/{guid}/permissions"),
                saveSettings: new TypedApiEndpoint<M.Abstract.Abstract>("/admin/abstract/{guid}/settings"),
                saveUploads: new TypedApiEndpoint<M.Abstract.Abstract>("/admin/abstract/{guid}/uploads"),
                search: new TypedApiEndpoint<M.Abstract.ScheduledAbstractItem[]>("/admin/abstract/search"),
                sendInvitationEmails: new TypedApiEndpoint<boolean>("/admin/abstract/{eventId}/send-invitation-emails/{abstractTypeId}"),
            },
            abstractType: {
                type: new TypedApiEndpoint<M.Abstract.AbstractTypeEdit>("/admin/abstract-type/{id}"),
                types: new TypedApiEndpoint<M.Abstract.AbstractTypeEdit[]>("/admin/abstract-type"),
            },
            abstractGroup: {
                group: new TypedApiEndpoint<M.Abstract.GradingGroup>("/admin/abstract-group/{id}"),
                list: new TypedApiEndpoint<M.Abstract.GradingGroup[]>("/admin/abstract-group"),
            },
            emailTemplate: {
                template: new TypedApiEndpoint<M.Email.EmailTemplate>("/admin/email-template/{id}"),
                templates: new TypedApiEndpoint<M.Email.EmailTemplate[]>("/admin/email-template"),
                types: new TypedApiEndpoint<M.Email.EmailTemplateType[]>("/admin/email-template/types"),
            },
            event: {
                childrenSequence: new TypedApiEndpoint<M.Event.SubEvent[]>("/admin/event/{eventId}/children-sequence"),
                event: new TypedApiEndpoint<M.Event.Event>("/admin/event/{eventId}"),
                exportCalendar: new TypedApiEndpoint<string>("/admin/event/{eventId}/export-calendar"),
                exportRsvp: new TypedApiEndpoint<string>("/admin/event/{eventId}/export-rsvp"),
                mainEvents: new TypedApiEndpoint<M.Event.EventAdminListItem[]>("/admin/event/main-events"),
                scheduleAbstracts: new TypedApiEndpoint<M.Event.SubEvent>("/admin/event/{eventId}/schedule-abstracts"),
                subEvent: new TypedApiEndpoint<M.Event.SubEvent>("/admin/event/{eventId}/sub-event"),
                subEvents: new TypedApiEndpoint<M.Event.SubEvent[]>("/admin/event/{eventId}/sub-events"),
                upload: new TypedApiEndpoint<M.Upload.Media>("/admin/event/upload"),
            },
            nav: {
                children: new ApiEndpoint("/admin/nav/children"),
                item: new ApiEndpoint("/admin/nav/item"),
                list: new ApiEndpoint("/admin/nav"),
                navigatableRecord: new ApiEndpoint("/admin/nav/navigatable-record"),
                searchNavigatableRecords: new ApiEndpoint("/admin/nav/search-navigatable-records"),
                redirect: new ApiEndpoint("/admin/nav/redirect"),
                separator: new ApiEndpoint("/admin/nav/separator"),
                types: new ApiEndpoint("/admin/nav/types"),
                updateSort: new ApiEndpoint("/admin/nav/update-sort"),
            },
            page: {
                list: new ApiEndpoint("/admin/page/list"),
                page: new ApiEndpoint("/admin/page"),
            },
            tag: {
                list: new TypedApiEndpoint<M.Tag.TagAdmin[]>("/admin/tag"),
                merge: new TypedApiEndpoint<M.Tag.TagAdmin>("/admin/tag/{id}/merge/{toId}"),
                tag: new TypedApiEndpoint<M.Tag.TagAdmin>("/admin/tag/{id}"),
            },
            user: {
                profilePicture: new TypedApiEndpoint<M.Upload.Media>("/admin/user/profile-picture"),
            },
        },
        // All endpoints that require member access permissions
        member: {
        },
        // All endpoints that require user access permissions
        users: {
            abstractGrading: {
                abstract: new TypedApiEndpoint<M.Abstract.Abstract>("/users/abstract-grading/abstract/{guid}"),
                abstracts: new TypedApiEndpoint<M.Abstract.AbstractGraderListItem[]>("/users/abstract-grading/abstracts"),
                abstractAccess: new TypedApiEndpoint<boolean>("/users/abstract-grading/abstract/{guid}/access"),
                grade: new TypedApiEndpoint<M.Abstract.Grade>("/users/abstract-grading/grade/{guid}"),
                reviewerStats: new TypedApiEndpoint<M.Abstract.ReviewerStats>("/users/abstract-grading/reviewer-stats"),
            },
            account: {
                profilePicture: new TypedApiEndpoint<M.Upload.Media>("/users/account/profile-picture"),
            },
            careerCenter: {
                createJob: new ApiEndpoint("/users/career-center/create-job"),
                createResume: new ApiEndpoint("/users/career-center/create-resume"),
                finalizeJob: new ApiEndpoint("/users/career-center/finalize-job"),
                finalizeResume: new ApiEndpoint("/users/career-center/finalize-resume"),
                job: new ApiEndpoint("/users/career-center/job"),
                jobs: new ApiEndpoint("/users/career-center/jobs"),
                jobSubscription: new ApiEndpoint("/users/career-center/job-subscription"),
                renewJob: new ApiEndpoint("/users/career-center/renew-job"),
                renewResume: new ApiEndpoint("/users/career-center/renew-resume"),
                resume: new ApiEndpoint("/users/career-center/resume"),
                resumes: new ApiEndpoint("/users/career-center/resumes"),
                resumeSubscription: new ApiEndpoint("/users/career-center/resume-subscription"),
                subscriptions: new ApiEndpoint("/users/career-center/subscriptions"),
                uploadCV: new ApiEndpoint("/users/career-center/upload-cv"),
            },
            financialDisclosure: {
                get: new TypedApiEndpoint<M.FinancialDisclosure.FD>("/users/financial-disclosure"),
            },
        },
        abstract: {
            abstract: new TypedApiEndpoint<M.Abstract.Abstract>("/abstract/{guid}"),
            abstracts: new TypedApiEndpoint<M.Abstract.Abstract[]>("/abstract"),
            composeAuthorship: new TypedApiEndpoint<M.Abstract.Abstract>("/abstract/compose/authorship"),
            composeDetails: new TypedApiEndpoint<M.Abstract.Abstract>("/abstract/compose/details"),
            composeReview: new TypedApiEndpoint<M.Abstract.Abstract>("/abstract/compose/review"),
            composeUploads: new TypedApiEndpoint<M.Abstract.Abstract>("/abstract/compose/uploads"),
            hasAccess: new TypedApiEndpoint<boolean>("/abstract/{guid}/has-access"),
            invitationResponse: new TypedApiEndpoint<boolean>("/abstract/{guid}/invitation-response"),
            media: new TypedApiEndpoint<M.Abstract.AbstractMedia>("/abstract/media/{id}"),
            sendFdRequest: new TypedApiEndpoint<M.Abstract.AbstractAuthor>("/abstract/{guid}/send-fd-request/{authorId}"),
            settings: new TypedApiEndpoint<M.Abstract.AbstractSettings>("/abstract/settings/{eventId}"),
            type: new TypedApiEndpoint<M.Abstract.AbstractType>("/abstract/type/{id}"),
            typeByAbstractGuid: new TypedApiEndpoint<M.Abstract.AbstractType>("/abstract/{guid}/type"),
            typeIdByAbstractGuid: new TypedApiEndpoint<number>("/abstract/{guid}/typeid"),
            types: new TypedApiEndpoint<M.Abstract.AbstractType[]>("/abstract/types/{eventId}"),
            upload: new TypedApiEndpoint<M.Upload.Media>("/abstract/{guid}/upload"),
            userStats: new TypedApiEndpoint<M.Abstract.UserStats>("/abstract/user-stats/{eventId}"),
        },
        account: {
            checkPasswordReset: new ApiEndpoint("/account/check-password-reset"),
            createAccount: new ApiEndpoint("/account/create-account"),
            requestPasswordReset: new ApiEndpoint("/account/request-password-reset"),
            resetPassword: new ApiEndpoint("/account/reset-password"),
        },
        careerCenter: {
            job: new ApiEndpoint("/career-center/job"),
            jobs: new ApiEndpoint("/career-center/jobs"),
            regions: new ApiEndpoint("/career-center/regions"),
            resume: new ApiEndpoint("/career-center/resume"),
            resumes: new ApiEndpoint("/career-center/resumes"),
        },
        country: {
            list: new ApiEndpoint("/countries"),
        },
        event: {
            list: new TypedApiEndpoint<M.Event.Event[]>("/event"),
            timezones: new TypedApiEndpoint<M.Generic.TimeZone[]>("/event/timezones"),
        },
        file: {
            tempFile: new ApiEndpoint("/file/{token}"),
        },
        media: {
            media: new ApiEndpoint("/media/media"),
        },
        members: {
            duesAmount: new TypedApiEndpoint<M.Member.DuesAmount>("/members/{userId}/dues-amount"),
            search: new TypedApiEndpoint<M.Member.BasicProfile[]>("/members/search"),
            status: new TypedApiEndpoint<M.Member.MemberStatus>("/members/{userId}/status"),
        },
        nav: {
            pathExists: new ApiEndpoint("/nav/path-exists", false),
            public: new ApiEndpoint("/nav/public", false),
        },
        org: {
            search: new ApiEndpoint("/org/search"),
        },
        page: {
            page: new ApiEndpoint("/page"),
        },
        seo: {
            tags: new ApiEndpoint("/seo/tags", false),
        },
        tag: {
            tags: new TypedApiEndpoint<M.Tag.Tag[]>("/tag"),
        },
        token: {
            authorize: new ApiEndpoint("/token/auth", false),
            check: new ApiEndpoint("/token/check"),
        },
        user: {
            basicProfile: new TypedApiEndpoint<M.User.BasicProfile>("/user/basic-profile"),
        },
    };

    private accessToken: string = "";

    private _apiUrl: string = Settings.ApiUrl;
    // private _servicesUrl: string = Settings.ServicesUrl;

    private _fetch: any = undefined; // typeof fetch !== "undefined" ? fetch : undefined;

    public constructor(stateOrToken?: AppState | string) {
        if (typeof stateOrToken === "string")
            this.accessToken = stateOrToken;
        else if (stateOrToken && stateOrToken.auth && stateOrToken.auth.accessToken)
            this.accessToken = stateOrToken.auth.accessToken;
    }

    /**
     * Sets the api URL
     * @param url The url
     */
    public setApiUrl(url: string) {
        this._apiUrl = url;
    }

    /**
     * Sets the services URL
     * @param url The url
     */
    // public setServicesUrl(url: string) {
    //     this._servicesUrl = url;
    // }

    /**
     * Sets the fetch method used internally to call api endpoints.
     * Useful for node express that doesn't know about browser's fetch/polyfill.
     * @param fetch The fetch methods
     */
    public setFetchMethod(fetch: any) {
        this._fetch = fetch;
    }

    /**
     * Performs a fetch call to the api.
     * @param {ApiEndpoint | RequestInfo} endpoint fetch request info, such as url
     * @param {RequestInit} [init] fetch request options such as headers, body, etc.
     * @returns {Promise<IResult<TData>>}
     */
    public async callApi<TData>(endpoint: ApiEndpoint | TypedApiEndpoint<TData> | RequestInfo, init?: RequestInitExtended): Promise<IResult<TData>> {
        try {
            let authorize = false;
            let input: RequestInfo;

            if (typeof endpoint === "string")
                input = endpoint;
            else if (typeof Request !== "undefined" && endpoint instanceof Request)
                input = endpoint;
            else {
                authorize = (endpoint as ApiEndpoint).auth;
                input = ApiEndpoint.getFormattedUrl(endpoint as ApiEndpoint);
            }

            // if api endpoint starts with "/", automatically prefix the url with the api host.
            if (typeof input === "string" && input.indexOf("/") === 0)
                input = `${this._apiUrl}${input}`;

            // append optional query parameters
            if (typeof input === "string" && init && init.queryParams)
                input += "?" + Utility.queryParamsToUrl(init.queryParams);

            const authAssigned = (init && Utility.containsKey(init.headers, "Authorization"));
            const contentTypeAssigned = (init && Utility.containsKey(init.headers, "Content-Type"));
            const isGet = !init || (init && (init.method || "get").toLowerCase() === "get");

            // automatically add auth header if not already assigned
            if (authorize && !authAssigned) {
                const headers = (init && init.headers)
                    ? init.headers
                    : {};

                init = {
                    ...init,
                    headers: {
                        ...headers,
                        Authorization: "Bearer " + this.accessToken,
                    },
                };
            }

            // automatically add "application/json" content-type header if not already assigned
            if (!isGet && !contentTypeAssigned) {
                const headers = (init && init.headers)
                    ? init.headers
                    : {};

                init = {
                    ...init,
                    headers: {
                        ...headers,
                        "Content-Type": "application/json",
                    },
                };
            }

            const response = await (this._fetch !== undefined ? this._fetch(input, init) : fetch(input, init));
            const respText = await response.text();

            Log.info("Api.callApi url", input, init, response);

            if (response.ok) {
                try {
                    const respJson = JSON.parse(respText);
                    Log.info("Api.callApi resp as json", respJson);
                    const obj = respJson as IResult<TData>;
                    return obj;
                }
                catch (parseErr) {
                    Log.info("Api.callApi resp as text", respText);
                    return new Result(respText as any);
                }
            }

            throw new Error(respText || response.statusText);
        }
        catch (err) {
            // error may be a json sent from server
            try {
                const errJson = JSON.parse(err.message || err);
                const obj = errJson as IResult<TData>;
                Log.warn("Api.callApi error obj", obj);
                return obj;
            }
            catch (catchErr) {
                const result = new Result<TData>(undefined, "Error");
                result.errors.push(err.message || err);
                result.message = err.message || err;
                result.isSuccess = false;
                Log.warn("Api.callApi error txt", err);
                return result;
            }
        }
    }

    /**
     * Performs a check against the api for access token validation
     * @returns {Promise<IResult<string>>}
     */
    public async authCheck(): Promise<IResult<string>> {
        return await this.callApi<string>(Api.endpoints.token.check);
    }

    /**
     * Authorizes a client against the api.
     * @param options Authorization options
     */
    public async authorize(options: ApiAuthorizeOptions): Promise<IResult<TokenAuthorizationResult>> {
        const data: any = {
            client_id: options.clientId,
            client_secret: options.clientSecret,
            username: options.username,
            password: options.password,
            legacyMember: options.legacyMember,
            legacyUser: options.legacyUser,
            grant_type: options.grantType,
            refresh_token: options.refreshToken,
        };

        const dataEncoded = Object.keys(data)
            .filter((key: string) => {
                return data[key] !== undefined && data[key] !== null;
            })
            .map((key: string) => {
                if (data[key] !== undefined)
                    return encodeURIComponent(key) + "=" + encodeURIComponent(data[key]);
                else
                    return "";
            }).join("&");

        const init: RequestInit = {
            method: "Post",
            body: dataEncoded,
            headers: {
                "Content-type": "application/x-www-form-urlencoded",
            },
        };

        const result = await this.callApi<TokenAuthorizationResult>(`${this._apiUrl}${Api.endpoints.token.authorize}`, init);

        // authorize legacy
        if (result.isSuccess && result.data && options.password && options.username)
            new LegacyApi().authorize(options.username, options.password).then();

        return result;
    }

    public async pingApi(): Promise<IResult<{}>> {
        return await this.callApi(`${Settings.ApiUrl}/ping`, { method: "HEAD" });
    }

    public async pingServices(): Promise<IResult<{}>> {
        return await this.callApi(`${Settings.ServicesUrl}/ping`, { method: "HEAD" });
    }

    /**
     * Performs a DELETE api call
     * @param {ApiEndpoint | RequestInfo} endpoint
     * @param {RequestInit} [init] fetch request options such as headers, body, etc.
     * @returns {Promise<IResult<TData>>}
     */
    public async delete<TData>(endpoint: ApiEndpoint | TypedApiEndpoint<TData> | RequestInfo, init?: RequestInitExtended): Promise<IResult<TData>> {
        init = {...init, method: "Delete" };
        return await this.callApi<TData>(endpoint, init);
    }

    /**
     * Performs a GET api call
     * @param {ApiEndpoint | RequestInfo} endpoint
     * @param {RequestInit} [init] fetch request options such as headers, body, etc.
     * @returns {Promise<IResult<TData>>}
     */
    public async get<TData>(endpoint: ApiEndpoint | TypedApiEndpoint<TData> | RequestInfo, init?: RequestInitExtended): Promise<IResult<TData>> {
        init = {...init, method: "Get" };
        return await this.callApi<TData>(endpoint, init);
    }

    /**
     * Performs a PATCH api call
     * @param {ApiEndpoint | RequestInfo} endpoint
     * @param {RequestInit} [init] fetch request options such as headers, body, etc.
     * @returns {Promise<IResult<TData>>}
     */
    public async patch<TData>(endpoint: ApiEndpoint | TypedApiEndpoint<TData> | RequestInfo, init?: RequestInitExtended): Promise<IResult<TData>> {
        init = {...init, method: "Patch" };
        return await this.callApi<TData>(endpoint, init);
    }

    /**
     * Performs a POST api call
     * @param {ApiEndpoint | RequestInfo} endpoint
     * @param {RequestInit} [init] fetch request options such as headers, body, etc.
     * @returns {Promise<IResult<TData>>}
     */
    public async post<TData>(endpoint: ApiEndpoint | TypedApiEndpoint<TData> | RequestInfo, init?: RequestInitExtended): Promise<IResult<TData>> {
        init = {...init, method: "Post" };
        return await this.callApi<TData>(endpoint, init);
    }

    /**
     * Performs a PUT api call
     * @param {ApiEndpoint | RequestInfo} endpoint
     * @param {RequestInit} [init] fetch request options such as headers, body, etc.
     * @returns {Promise<IResult<TData>>}
     */
    public async put<TData>(endpoint: ApiEndpoint | TypedApiEndpoint<TData> | RequestInfo, init?: RequestInitExtended): Promise<IResult<TData>> {
        init = {...init, method: "Put" };
        return await this.callApi<TData>(endpoint, init);
    }

    /**
     * Saves the given access token in the server session (see server/routes-token.js)
     * @param accessToken The access token
     */
    public async saveTokenToSession(accessToken: string) {
        // no need to do this in local mode
        if (!Settings.DoSessionSave)
            return;

        try {
            const init: RequestInit = {
                body: `token=${accessToken}`,
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
                credentials: "include", // need this in order to send cookie state
                method: "Post",
            };

            const url = "/token/set";
            const response = await (this._fetch !== undefined ? this._fetch(url, init) : fetch(url, init));
            const respText = await response.text();

            if (response.ok) {
                try {
                    const respJson = JSON.parse(respText);
                    Log.info("saveTokenToSession resp as json", respJson);
                    const obj = respJson as IResult<string>;
                    return obj;
                }
                catch (parseErr) {
                    Log.info("saveTokenToSession resp as text", respText);
                    return new Result(respText);
                }
            }

            throw new Error(respText || response.statusText);
        }
        catch (err) {
            console.warn("Unable to save token to session", err);
            return new Result("Unable to save token to session " + err);
        }
    }
}



// WEBPACK FOOTER //
// ./src/services/Api.ts